import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Receipt, Bank, FileText, ShieldCheck } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Nav */}
      <header className="border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#0A66C2] text-white grid place-items-center font-extrabold text-sm">L</div>
            <div className="font-display font-extrabold text-lg tracking-tight">LedgerBook Cloud</div>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/login" data-testid="landing-login" className="hover:underline">Sign in</Link>
            <Link to="/register" data-testid="landing-register" className="lb-btn lb-btn-sm">Start free →</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden lb-grid-bg">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">v1.2 · GST + e-Invoice + Bank Recon</div>
            <h1 className="font-display font-extrabold text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-3">
              Serious accounting.<br />
              <span className="bg-[#E7F3FF] text-[#0A66C2] px-2 -mx-2">Zero guesswork.</span>
            </h1>
            <p className="text-neutral-600 mt-6 max-w-xl">
              A multi-tenant billing & books platform for Indian SMBs and CA firms.
              GST-compliant invoices with IRN + QR, GSTR-1/3B/2B filing, and one-click bank reconciliation — all on true double-entry.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/register" className="lb-btn" data-testid="hero-cta">
                Start free trial <ArrowRight size={14} />
              </Link>
              <Link to="/login" className="lb-btn lb-btn-ghost" data-testid="hero-login">
                Sign in
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md text-sm">
              {["No credit card", "Cancel anytime", "GSP integration ready"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-neutral-600">
                  <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                  {t}
                </div>
              ))}
            </div>
          </div>

          {/* Right dashboard mock */}
          <div className="lg:col-span-5 relative">
            <div className="lb-card p-4 shadow-none">
              <div className="flex items-center justify-between border-b border-neutral-200 pb-3 mb-3">
                <div className="text-xs font-semibold tracking-wide">DASHBOARD · APR</div>
                <div className="text-[10px] font-mono text-neutral-500">27ABCDE1234F1Z5</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { l: "Revenue", v: "₹18,42,300", c: "text-emerald-600" },
                  { l: "GST Payable", v: "₹2,81,540", c: "text-[#0A66C2]" },
                  { l: "Outstanding", v: "₹3,20,000", c: "text-rose-600" },
                  { l: "Cash Position", v: "₹9,15,220", c: "text-slate-800" },
                ].map((k) => (
                  <div key={k.l} className="border border-neutral-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{k.l}</div>
                    <div className={"font-mono font-bold text-lg mt-1 " + k.c}>{k.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-t border-neutral-200 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">e-Invoice IRN</div>
                <div className="font-mono text-[10px] break-all text-neutral-700">
                  a7c4f9b2e18d3f6a5c8b1e4d7f2a9c6b8e1d4f7a2c5b8e1d4f7a2c5b8e1d4f7a
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-neutral-200 bg-[#F4F4F5]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-4">
          {[
            { i: Receipt, t: "e-Invoice + IRN", d: "Auto-generate IRN, ack no. and QR for every B2B invoice via GSP." },
            { i: FileText, t: "GSTR Filing", d: "One-click GSTR-1, 3B, 2B preview with mismatch reconciliation." },
            { i: Bank, t: "Bank Recon", d: "Upload CSV/Excel or auto-sync. Match suggestions & voucher creation." },
            { i: ShieldCheck, t: "Audit-ready", d: "Immutable audit log for every compliance action. Roles included." },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="lb-card p-5 lb-card-hover">
              <Icon size={22} />
              <div className="font-display font-bold text-lg mt-3">{t}</div>
              <div className="text-sm text-neutral-600 mt-2">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-6 text-center text-xs text-neutral-500">
        © LedgerBook Cloud · Made in India · GST-compliant billing & books
      </footer>
    </div>
  );
}
